import React, { useEffect, useCallback, useRef } from 'react';
import { Fade, Box } from '@televet/kibble-ui/build/chakra';
import Launcher from './Launcher';
import WidgetPreview from './WidgetPreview';
import WidgetExpanded from './WidgetExpanded';
import { EventNames, useTracking } from 'shared/providers/Tracking';
import { useWidgetStore } from 'state/useWidgetStore';
import { ClinicWidgetRequestTypeResponse, ClinicWidgetSettingResponse } from 'shared/types';
import { WindowMessageEvent } from 'shared/enums/WindowMessageEvent';

interface ClinicSiteWidgetProps {
  widgetSettings: ClinicWidgetSettingResponse;
}

const ClinicSiteWidget = ({ widgetSettings }: ClinicSiteWidgetProps): JSX.Element | null => {
  const { isInline, isMobile, isOpen, showPreview, onToggle, setSelectedRequest, goToNextStep } = useWidgetStore(
    (state) => state,
  );
  const applicationContainerRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useTracking();

  const handleSelectRequestByIdParentEvent = useCallback(
    (e: MessageEvent): void => {
      if (e?.data?.event === WindowMessageEvent.SelectRequestById && e?.data?.payload.selectedRequestId) {
        const matchingRequest = widgetSettings?.clinicWidgetRequestTypes.find(
          (request: ClinicWidgetRequestTypeResponse) => request?.id === e.data.payload.selectedRequestId,
        );

        if (matchingRequest) {
          setSelectedRequest(matchingRequest);
          goToNextStep();
        } else {
          console.error(`No matching request found for id: ${e.data.payload.selectedRequestId}`);
        }
      }
    },
    [widgetSettings?.clinicWidgetRequestTypes, setSelectedRequest, goToNextStep],
  );

  // Listen for new message events
  useEffect(() => {
    window.addEventListener('message', (e: MessageEvent) => {
      handleSelectRequestByIdParentEvent(e);
    });

    return (): void => {
      window.removeEventListener('message', handleSelectRequestByIdParentEvent);
    };
    // eslint-disable-next-line
  }, []);

  // Toggle widget open when showPreview and isOpen are true
  useEffect(() => {
    if (showPreview && isOpen) {
      onToggle(true);
    }
  }, [isOpen, onToggle, showPreview]);

  return (
    <Box
      className="Widget"
      display="inline-block"
      ref={applicationContainerRef}
      p={isMobile || isInline ? 0 : 3}
      w={(isMobile || isInline) && isOpen ? '100%' : 'auto'}
      minW="92px"
      maxW={(isMobile || isInline) && isOpen ? '100%' : 'unset'}
      h={isInline ? '100vh' : 'auto'}
      minH="92px"
      maxH={isMobile || isInline ? '100%' : 'unset'}
      data-iframe-size
    >
      {isOpen && <WidgetExpanded widgetSetting={widgetSettings} />}

      {showPreview && (
        <Fade
          className="WidgetPreview__FadeContainer"
          in={showPreview}
          transition={{ exit: { duration: 0.5 }, enter: { duration: 0.5 } }}
        >
          <WidgetPreview
            data-iframe-size
            widgetSetting={widgetSettings}
            onCloseClick={(): void => {
              trackEvent(EventNames.DISMISS_X_BUTTON);
              onToggle(false);
            }}
          />
        </Fade>
      )}

      {!(isMobile && isOpen) && !isInline && (
        <Fade className="WidgetPreview__FadeContainer" in={true} transition={{ enter: { duration: 0.5 } }}>
          <Launcher
            onToggleWidget={(): void => {
              if (isOpen) {
                trackEvent(EventNames.DIMISS_V_ICON);
              } else {
                trackEvent(EventNames.CHAT_BUBBLE_CLICK, { isPreview: showPreview });
              }
              onToggle();
            }}
          />
        </Fade>
      )}
    </Box>
  );
};

export default ClinicSiteWidget;
